import React, { useEffect, useState } from 'react';

const ranges = ['24h', '7d', '30d', '1y'];

const formatCurrency = (value) => `$${Number(value ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}`;

const PortfolioDashboard = () => {
  const [performance, setPerformance] = useState(null);
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('7d');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const perfRes = await fetch('/api/portfolio/performance');
        const chartRes = await fetch(`/api/portfolio/chart?range=${range}`);
        if (!perfRes.ok || !chartRes.ok) throw new Error('Erreur réseau');
        setPerformance(await perfRes.json());
        setChart(await chartRes.json());
      } catch (e) {
        setError('Impossible de récupérer les données du portefeuille.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [range]);

  const mockPerformance = {
    portfolioValue: 10000,
    today: { pnl: 120, percent: 1.2 },
    week: { pnl: 350, percent: 3.5 },
    month: { pnl: 900, percent: 9.0 },
    allTime: { pnl: 4200, percent: 42.0 },
  };
  const mockChart = [
    { value: 8000 }, { value: 8200 }, { value: 8500 }, { value: 9000 }, { value: 9500 }, { value: 10000 },
  ];

  const effectivePerformance = performance || mockPerformance;
  const effectiveChart = Array.isArray(chart) && chart.length > 0 ? chart : mockChart;
  const usingMockData = Boolean(error || !performance);

  if (loading) {
    return (
      <div className="portfolio-dashboard-compact portfolio-dashboard-compact--loading">
        Chargement du dashboard portefeuille...
      </div>
    );
  }

  return (
    <div className="portfolio-dashboard-compact">
      <div className="portfolio-dashboard-compact__hero">
        <div>
          <div className="portfolio-dashboard-compact__eyebrow">Contexte portefeuille</div>
          <h3>Performance embarquée</h3>
          <p>
            Cette vue compacte garde le portefeuille à portée de regard pendant la lecture du scanner d’actifs,
            sans basculer sur le dashboard complet.
          </p>
        </div>
        <div className="portfolio-dashboard-compact__range-switch">
          {ranges.map((currentRange) => (
            <button
              key={currentRange}
              type="button"
              className={`portfolio-dashboard-compact__range-button${currentRange === range ? ' portfolio-dashboard-compact__range-button--active' : ''}`}
              onClick={() => setRange(currentRange)}
            >
              {currentRange}
            </button>
          ))}
        </div>
      </div>

      {usingMockData ? (
        <div className="portfolio-dashboard-compact__warning">
          {error || 'Données portefeuille réelles indisponibles. Affichage simulé.'}
        </div>
      ) : null}

      <div className="portfolio-dashboard-compact__stats">
        <div className="portfolio-dashboard-compact__stat-card">
          <span className="portfolio-dashboard-compact__stat-label">Valeur portefeuille</span>
          <strong>{formatCurrency(effectivePerformance.portfolioValue)}</strong>
        </div>
        <div className="portfolio-dashboard-compact__stat-card">
          <span className="portfolio-dashboard-compact__stat-label">Aujourd’hui</span>
          <strong>{`${effectivePerformance.today.pnl >= 0 ? '+' : ''}${formatCurrency(effectivePerformance.today.pnl)}`}</strong>
          <span>{`${effectivePerformance.today.percent}%`}</span>
        </div>
        <div className="portfolio-dashboard-compact__stat-card">
          <span className="portfolio-dashboard-compact__stat-label">7 jours</span>
          <strong>{`${effectivePerformance.week.pnl >= 0 ? '+' : ''}${formatCurrency(effectivePerformance.week.pnl)}`}</strong>
          <span>{`${effectivePerformance.week.percent}%`}</span>
        </div>
        <div className="portfolio-dashboard-compact__stat-card">
          <span className="portfolio-dashboard-compact__stat-label">30 jours / all time</span>
          <strong>{`${effectivePerformance.month.pnl >= 0 ? '+' : ''}${formatCurrency(effectivePerformance.month.pnl)}`}</strong>
          <span>{`All time ${effectivePerformance.allTime.percent}%`}</span>
        </div>
      </div>

      <div className="portfolio-dashboard-compact__chart-surface">
        <PortfolioChart data={effectiveChart} />
      </div>
    </div>
  );
};

const PortfolioChart = ({ data }) => {
  if (!data.length) return <div className="portfolio-dashboard-compact__chart-empty">Aucune donnée pour le graphique.</div>;

  return (
    <svg width="100%" height="220" viewBox="0 0 900 220" className="portfolio-dashboard-compact__chart">
      {data.map((point, i, arr) => {
        if (i === 0) return null;
        const prev = arr[i - 1];
        const x1 = (i - 1) * (900 / arr.length);
        const x2 = i * (900 / arr.length);
        const y1 = 220 - (prev.value / Math.max(...arr.map(p => p.value)) * 200);
        const y2 = 220 - (point.value / Math.max(...arr.map(p => p.value)) * 200);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#214f77" strokeWidth={3} />;
      })}
    </svg>
  );
};

export default PortfolioDashboard;
